// src/components/digital_twin/DigitalTwinLayerManager.js
// ═══════════════════════════════════════════════════════════════════════════
// Enhanced Digital Twin Layer Manager — Enterprise Grade
// ═══════════════════════════════════════════════════════════════════════════
// All existing logic preserved + enterprise enhancements:
//   - Layer groups with expand/collapse
//   - Search and filter layers
//   - Bulk layer operations
//   - Layer presets/templates
//   - Export/import layer configurations
//   - Layer opacity controls
//   - Recently used layers
//   - Layer statistics
//   - Better animations and transitions
//   - Enhanced accessibility
// ═══════════════════════════════════════════════════════════════════════════
// --- UPDATE (Digital Twin MOW+ v1) ---------------------------------------------------------
//   FIXES
//   • Presets and Show/Hide All called onLayerToggle(layer, value) for every layer, but the
//     page's handler toggled (ignored the value) and read stale state — presets left the wrong
//     layers on and Hide All inverted mixed states. Only layers whose state differs are now
//     changed, so the result is right with either kind of parent handler (the page handler now
//     also honours the value).
//   • "Save as Preset" had no handler — custom presets are saved (this browser) and listed with the
//     built-in ones; they can be deleted. "Import Configuration" added (the header promised it).
//   • Recently used showed raw keys ("openWorkOrders") — labels now.
//   • Layer opacity is not implemented (needs scene access) — header item kept for reference.
//   • Layers with no data loaded show "—" instead of no badge, so 0 and "not loaded" differ
//     (pass `unavailableLayers` to grey them out with the reason).
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useMemo } from 'react';
import {
  Box, VStack, Heading, Icon, Flex, Text, Button, Tooltip, 
  Divider, Switch, Badge, useColorModeValue, HStack, IconButton,
  Input, InputGroup, InputLeftElement, Menu, MenuButton, MenuList,
  MenuItem, Collapse, useDisclosure, Slider, SliderTrack, SliderFilledTrack,
  SliderThumb, useToast, Portal, SimpleGrid, Stat, StatLabel, StatNumber,
} from '@chakra-ui/react';
import {
  FaCity, FaWater, FaSatellite, FaExclamationTriangle, FaBell,
  FaTools, FaUserMd, FaStreetView, FaLayerGroup, FaEye, FaEyeSlash,
  FaSearch, FaDownload, FaUpload, FaSave, FaChevronDown, FaChevronUp,
  FaClock, FaFilter, FaExpand, FaCompress,
  FaMountain, // --- UPDATE (twin v8)
} from 'react-icons/fa';
import { MdMoreVert } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// --- UPDATE: custom presets (per browser) + helpers
const CUSTOM_PRESETS_KEY = 'flowsense.twin.layerPresets.v1';
const readCustomPresets = () => { try { const v = JSON.parse(window.localStorage.getItem(CUSTOM_PRESETS_KEY) || '{}'); return v && typeof v === 'object' ? v : {}; } catch (_) { return {}; } };
const writeCustomPresets = (v) => { try { window.localStorage.setItem(CUSTOM_PRESETS_KEY, JSON.stringify(v)); return true; } catch (_) { return false; } };
// --- END UPDATE

// ═════════════════════════════════════════════════════════════════════════════
// LAYER CONFIGURATION (preserved from original)
// ═════════════════════════════════════════════════════════════════════════════
const layerConfig = {
  baseLayers: {
    groupName: 'Base Network',
    layers: {
      assets: {
        label: 'Assets',
        icon: FaCity,
        description: 'Buildings, substations, and infrastructure nodes'
      },
      pipelines: {
        label: 'Pipelines',
        icon: FaWater,
        description: 'Water, sewer, and utility lines'
      },
      sensors: {
        label: 'Sensors',
        icon: FaSatellite,
        description: 'IoT devices reporting live data'
      },
    }
  },
  operationalLayers: {
    groupName: 'Operational Data',
    layers: {
      alerts: {
        label: 'Active Alerts',
        icon: FaBell,
        description: 'Real-time system warnings or events'
      },
      issues: {
        label: 'Reported Issues',
        icon: FaExclamationTriangle,
        description: 'Reported problems or maintenance flags'
      },
      openWorkOrders: {
        label: 'Open Work Orders',
        icon: FaTools,
        description: 'All dispatched or pending work orders'
      },
      predictedFailures: {
        label: 'Predicted Failures',
        icon: FaUserMd,
        description: 'Assets AI predicts will fail soon (RUL < 7 days)'
      },
      technicianLocations: {
        label: 'Technician Locations',
        icon: FaStreetView,
        description: 'Live location of all active field technicians'
      },
    }
  },
  // --- UPDATE (twin v8): TessaDEM terrain
  terrainLayers: {
    groupName: 'Terrain & Drainage',
    layers: {
      terrain: {
        label: 'Ground Elevation',
        icon: FaMountain,
        description: 'TessaDEM ground height under every asset; rings mark terrain lows where run-off collects'
      },
    }
  },
  // --- END UPDATE
};

// ═════════════════════════════════════════════════════════════════════════════
// LAYER PRESETS
// ═════════════════════════════════════════════════════════════════════════════
const layerPresets = {
  default: {
    name: 'Default View',
    description: 'Standard operational view',
    layers: ['assets', 'pipelines', 'sensors', 'alerts']
  },
  maintenance: {
    name: 'Maintenance Mode',
    description: 'Focus on work orders and issues',
    layers: ['assets', 'issues', 'openWorkOrders', 'technicianLocations']
  },
  monitoring: {
    name: 'Monitoring Mode',
    description: 'Real-time system monitoring',
    layers: ['sensors', 'alerts', 'predictedFailures']
  },
  planning: {
    name: 'Planning Mode',
    description: 'Infrastructure planning view',
    layers: ['assets', 'pipelines', 'predictedFailures']
  },
  // --- UPDATE (twin v8)
  flood: {
    name: 'Flood & Drainage',
    description: 'Ground elevation, wells, sensors and alerts for storm response',
    layers: ['assets', 'sensors', 'alerts', 'terrain', 'predictedFailures']
  },
  // --- END UPDATE
};

// ═════════════════════════════════════════════════════════════════════════════
// ANIMATION VARIANTS
// ═════════════════════════════════════════════════════════════════════════════
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 }
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const DigitalTwinLayerManager = ({ layerVisibility, onLayerToggle, layerCounts = {}, unavailableLayers = {} }) => { // --- UPDATE: + unavailableLayers
  const toast = useToast();
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [recentLayers, setRecentLayers] = useState([]);
  const [isCompact, setIsCompact] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    baseLayers: true,
    operationalLayers: true,
    terrainLayers: true, // --- UPDATE (twin v8)
  });

  // Disclosure hooks
  const { isOpen: isStatsOpen, onToggle: onStatsToggle } = useDisclosure();
  // --- UPDATE: custom presets + import
  const [customPresets, setCustomPresets] = useState(readCustomPresets);
  const [presetName, setPresetName] = useState('');
  const { isOpen: isSaveOpen, onToggle: onSaveToggle, onClose: onSaveClose } = useDisclosure();
  const fileRef = React.useRef(null);
  const labelOf = React.useCallback((key) => { for (const g of Object.values(layerConfig)) if (g.layers[key]) return g.layers[key].label; return key; }, []);
  // --- END UPDATE

  // Theme styling (preserved from original)
  const cardBg = useColorModeValue("white", "gray.700");
  const sectionBg = useColorModeValue("gray.50", "gray.800");
  const hoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const searchBg = useColorModeValue("white", "gray.600");
  const headerGradient = useColorModeValue(
    'linear(to-r, purple.600, blue.500)',
    'linear(to-r, purple.400, blue.400)'
  );

  // Computed values
  const allVisible = Object.values(layerVisibility).every(Boolean);
  const visibleCount = Object.values(layerVisibility).filter(Boolean).length;
  const totalCount = Object.keys(layerVisibility).length;

  // Filter layers based on search
  const filteredLayers = useMemo(() => {
    if (!searchQuery) return layerConfig;

    const filtered = {};
    Object.entries(layerConfig).forEach(([groupKey, group]) => {
      const matchedLayers = {};
      Object.entries(group.layers).forEach(([layerKey, layer]) => {
        if (
          layer.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          layer.description.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          matchedLayers[layerKey] = layer;
        }
      });
      if (Object.keys(matchedLayers).length > 0) {
        filtered[groupKey] = { ...group, layers: matchedLayers };
      }
    });
    return filtered;
  }, [searchQuery]);

  // Stats
  const layerStats = useMemo(() => {
    const stats = {
      totalItems: 0,
      visibleItems: 0,
      criticalAlerts: layerCounts.alerts || 0,
      activeIssues: layerCounts.issues || 0,
    };

    Object.keys(layerVisibility).forEach(key => {
      const count = layerCounts[key] || 0;
      stats.totalItems += count;
      if (layerVisibility[key]) {
        stats.visibleItems += count;
      }
    });

    return stats;
  }, [layerVisibility, layerCounts]);

  // Handlers (preserved from original + enhanced)
  // --- UPDATE: apply a target visibility — only layers that differ are changed
  const applyVisibility = (target) => {
    Object.keys(layerVisibility).forEach((layer) => {
      const want = !!target[layer];
      if (!!layerVisibility[layer] !== want) onLayerToggle(layer, want);
    });
  };
  // --- END UPDATE

  const handleToggleAll = () => {
    // --- UPDATE: only layers that differ (a toggling parent inverted mixed states)
    const newState = !allVisible;
    applyVisibility(Object.fromEntries(Object.keys(layerVisibility).map((l) => [l, newState])));
    // --- END UPDATE

    toast({
      title: allVisible ? 'All layers hidden' : 'All layers visible',
      status: 'info',
      duration: 2000,
      position: 'top-right',
    });
  };

  const handleLayerToggle = (key) => {
    const newState = !layerVisibility[key];
    console.log(`[LayerManager] Toggling "${key}" to ${newState}`);
    onLayerToggle(key, newState);

    // Track recent layers
    if (newState && !recentLayers.includes(key)) {
      setRecentLayers(prev => [key, ...prev.slice(0, 4)]);
    }
  };

  const handleApplyPreset = (presetKey) => {
    const preset = layerPresets[presetKey] || customPresets[presetKey]; // --- UPDATE: + custom
    if (!preset) return;
    
    // --- UPDATE: one pass — hide-all-then-show toggled preset layers twice with a toggling parent
    const want = new Set(preset.layers);
    applyVisibility(Object.fromEntries(Object.keys(layerVisibility).map((l) => [l, want.has(l)])));
    // --- END UPDATE

    toast({
      title: `Applied ${preset.name}`,
      description: preset.description,
      status: 'success',
      duration: 3000,
      position: 'top-right',
    });
  };

  const handleExportConfig = () => {
    const config = {
      layerVisibility,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };

    const dataStr = JSON.stringify(config, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `layer-config-${Date.now()}.json`);
    linkElement.click();

    toast({
      title: 'Configuration exported',
      status: 'success',
      duration: 2000,
      position: 'top-right',
    });
  };

  // --- UPDATE: save / delete custom presets, import configuration
  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    const key = `custom_${name.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40)}`;
    const next = { ...customPresets, [key]: { name, description: 'Saved in this browser', layers: Object.keys(layerVisibility).filter((k) => layerVisibility[k]), custom: true } };
    setCustomPresets(next);
    const ok = writeCustomPresets(next);
    setPresetName(''); onSaveClose();
    toast({ title: `Preset "${name}" saved`, description: ok ? undefined : 'Browser storage is off — kept until you leave this page.', status: ok ? 'success' : 'warning', duration: 2500, position: 'top-right' });
  };
  const handleDeletePreset = (key) => {
    const next = { ...customPresets }; delete next[key];
    setCustomPresets(next); writeCustomPresets(next);
  };
  const handleImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const cfg = JSON.parse(await file.text());
      const vis = cfg && cfg.layerVisibility;
      if (!vis || typeof vis !== 'object') throw new Error('No layerVisibility in the file');
      const known = Object.fromEntries(Object.keys(layerVisibility).map((k) => [k, !!vis[k]]));
      applyVisibility(known);
      toast({ title: 'Configuration imported', status: 'success', duration: 2000, position: 'top-right' });
    } catch (err) {
      toast({ title: 'Import failed', description: err.message, status: 'error', duration: 4000, position: 'top-right' });
    }
  };
  // --- END UPDATE

  const toggleGroupExpand = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Render layer group
  const renderLayerGroup = (groupKey, group) => {
    const isExpanded = expandedGroups[groupKey];

    return (
      <Box key={groupKey} mb={4}>
        {/* Group Header */}
        <Flex
          align="center"
          justify="space-between"
          mb={3}
          cursor="pointer"
          onClick={() => toggleGroupExpand(groupKey)}
          _hover={{ opacity: 0.8 }}
        >
          <HStack spacing={2}>
            <Icon 
              as={isExpanded ? FaChevronUp : FaChevronDown} 
              boxSize={3} 
              color={mutedText}
            />
            <Text 
              fontSize="xs" 
              fontWeight="bold" 
              color={mutedText} 
              textTransform="uppercase" 
              letterSpacing="wider"
            >
              {group.groupName}
            </Text>
            <Badge colorScheme="purple" fontSize="2xs" borderRadius="full">
              {Object.keys(group.layers).length}
            </Badge>
          </HStack>
        </Flex>

        {/* Layers */}
        <Collapse in={isExpanded} animateOpacity>
          <MotionBox
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <VStack spacing={2} align="stretch">
              {Object.entries(group.layers).map(([key, { label, icon, description }]) => {
                const isVisible = layerVisibility[key] || false;
                const count = layerCounts[key];
                const unavailable = unavailableLayers[key]; // --- UPDATE

                return (
                  <MotionBox key={key} variants={itemVariants}>
                    <Tooltip label={unavailable ? `${description} — ${unavailable}` : description} placement="right" hasArrow>{/* --- UPDATE */}
                      <MotionFlex
                        align="center"
                        justify="space-between"
                        w="100%"
                        p={isCompact ? 2 : 3}
                        bg={sectionBg}
                        borderWidth="1px"
                        borderColor={isVisible ? "purple.400" : borderColor}
                        borderRadius="md"
                        cursor="pointer"
                        transition="all 0.2s"
                        _hover={{ bg: hoverBg, borderColor: "purple.300", transform: 'translateX(4px)' }}
                        onClick={() => handleLayerToggle(key)}
                        role="switch" aria-checked={!!isVisible} aria-label={`${label} layer`} opacity={unavailable ? 0.6 : 1} // --- UPDATE
                        boxShadow={isVisible ? "sm" : "none"}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <HStack spacing={isCompact ? 2 : 3}>
                          <Flex 
                            align="center" 
                            justify="center" 
                            w={isCompact ? "28px" : "32px"}
                            h={isCompact ? "28px" : "32px"}
                            borderRadius="md" 
                            bg={isVisible ? "purple.500" : "gray.500"}
                            color="white"
                            transition="all 0.2s"
                            boxShadow={isVisible ? "md" : "sm"}
                          >
                            <Icon as={icon} boxSize={isCompact ? 3 : 4} />
                          </Flex>
                          {!isCompact && (
                            <Text fontWeight="600" fontSize="sm" color={textColor}>
                              {label}
                            </Text>
                          )}
                        </HStack>

                        <HStack spacing={2}>
                          {count === undefined && <Badge variant="outline" colorScheme="gray" borderRadius="full" px={2} fontSize="xs">—</Badge>}{/* --- UPDATE: not loaded */}
                          {count !== undefined && (
                            <Badge 
                              colorScheme={count > 0 ? (isVisible ? "purple" : "gray") : "gray"}
                              variant={isVisible ? "solid" : "subtle"}
                              borderRadius="full"
                              px={2}
                              fontSize="xs"
                              fontWeight="700"
                            >
                              {count}
                            </Badge>
                          )}
                          {!isCompact && (
                            <Switch 
                              isChecked={isVisible} 
                              colorScheme="purple" 
                              pointerEvents="none"
                              size="sm"
                            />
                          )}
                        </HStack>
                      </MotionFlex>
                    </Tooltip>
                  </MotionBox>
                );
              })}
            </VStack>
          </MotionBox>
        </Collapse>

        <Divider mt={4} borderColor={borderColor} />
      </Box>
    );
  };

  return (
    <Box w="100%" bg={cardBg} borderRadius="xl" boxShadow="lg" borderWidth="1px" borderColor={borderColor}>
      {/* Header */}
      <Flex align="center" justify="space-between" p={4} borderBottomWidth="2px" borderColor={borderColor}>
        <HStack spacing={2}>
          <Icon as={FaLayerGroup} color="purple.500" boxSize={5} />
          <Heading size="md" bgGradient={headerGradient} bgClip="text" fontWeight="800">
            Layer Manager
          </Heading>
          <Badge colorScheme="purple" fontSize="xs" borderRadius="full">
            {visibleCount}/{totalCount}
          </Badge>
        </HStack>

        <HStack spacing={1}>
          <Tooltip label={isCompact ? "Expand view" : "Compact view"} hasArrow>
            <IconButton
              icon={<Icon as={isCompact ? FaExpand : FaCompress} />}
              size="sm"
              variant="ghost"
              onClick={() => setIsCompact(!isCompact)}
              aria-label="Toggle compact"
            />
          </Tooltip>

          <Menu>
            <MenuButton
              as={IconButton}
              icon={<MdMoreVert />}
              size="sm"
              variant="ghost"
              aria-label="More options"
            />
            <Portal>
              <MenuList fontSize="sm">
                <MenuItem icon={<FaDownload />} onClick={handleExportConfig}>
                  Export Configuration
                </MenuItem>
                <MenuItem icon={<FaSave />} onClick={onSaveToggle}>{/* --- UPDATE: handler */}
                  Save as Preset
                </MenuItem>
                {/* --- UPDATE */}
                <MenuItem icon={<FaUpload />} onClick={() => fileRef.current?.click()}>Import Configuration</MenuItem>
                {/* --- END UPDATE */}
                <Divider />
                {Object.entries(layerPresets).map(([key, preset]) => (
                  <MenuItem 
                    key={key} 
                    onClick={() => handleApplyPreset(key)}
                    fontSize="xs"
                  >
                    {preset.name}
                  </MenuItem>
                ))}
                {/* --- UPDATE: custom presets */}
                {Object.keys(customPresets).length > 0 && <Divider />}
                {Object.entries(customPresets).map(([key, preset]) => (
                  <MenuItem key={key} onClick={() => handleApplyPreset(key)} fontSize="xs" closeOnSelect>
                    <HStack justify="space-between" w="100%"><Text>{preset.name}</Text>
                      <IconButton as="span" size="xs" variant="ghost" icon={<FaEyeSlash />} aria-label={`Delete preset ${preset.name}`} onClick={(e) => { e.stopPropagation(); handleDeletePreset(key); }} /></HStack>
                  </MenuItem>
                ))}
                {/* --- END UPDATE */}
              </MenuList>
            </Portal>
          </Menu>
        </HStack>
      </Flex>

      <Box p={4}>
        {/* --- UPDATE: save-preset row + hidden import input */}
        <input ref={fileRef} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={handleImportFile} aria-label="Import layer configuration" />
        <Collapse in={isSaveOpen} animateOpacity>
          <HStack mb={3}>
            <Input size="sm" placeholder="Preset name" value={presetName} onChange={(e) => setPresetName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }} aria-label="Preset name" maxLength={40} />
            <Button size="sm" colorScheme="purple" onClick={handleSavePreset} isDisabled={!presetName.trim()}>Save</Button>
          </HStack>
        </Collapse>
        {/* --- END UPDATE */}
        {/* Search */}
        <InputGroup size="sm" mb={4}>
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color={mutedText} boxSize={3} />
          </InputLeftElement>
          <Input
            placeholder="Search layers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg={searchBg}
            borderRadius="md"
            borderColor={borderColor}
            _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px purple.400' }}
          />
        </InputGroup>

        {/* Quick Actions */}
        <SimpleGrid columns={2} spacing={2} mb={4}>
          <Button
            size="sm"
            colorScheme={allVisible ? "gray" : "purple"}
            variant={allVisible ? "outline" : "solid"}
            leftIcon={<Icon as={allVisible ? FaEyeSlash : FaEye} boxSize={3} />}
            onClick={handleToggleAll}
            fontWeight="600"
            fontSize="xs"
          >
            {allVisible ? 'Hide All' : 'Show All'}
          </Button>

          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            leftIcon={<Icon as={FaFilter} boxSize={3} />}
            onClick={onStatsToggle}
            fontWeight="600"
            fontSize="xs"
          >
            Stats
          </Button>
        </SimpleGrid>

        {/* Statistics Panel */}
        <Collapse in={isStatsOpen} animateOpacity>
          <Box mb={4} p={3} bg={sectionBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
            <SimpleGrid columns={2} spacing={3}>
              <Stat size="sm">
                <StatLabel fontSize="2xs" color={mutedText}>Total Items</StatLabel>
                <StatNumber fontSize="lg" color={textColor}>{layerStats.totalItems}</StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="2xs" color={mutedText}>Visible</StatLabel>
                <StatNumber fontSize="lg" color="purple.500">{layerStats.visibleItems}</StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="2xs" color={mutedText}>Alerts</StatLabel>
                <StatNumber fontSize="lg" color="red.500">{layerStats.criticalAlerts}</StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="2xs" color={mutedText}>Issues</StatLabel>
                <StatNumber fontSize="lg" color="orange.500">{layerStats.activeIssues}</StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>
        </Collapse>

        {/* Recent Layers */}
        {recentLayers.length > 0 && !isCompact && (
          <Box mb={4}>
            <HStack mb={2}>
              <Icon as={FaClock} boxSize={3} color={mutedText} />
              <Text fontSize="2xs" fontWeight="bold" color={mutedText} textTransform="uppercase">
                Recently Used
              </Text>
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              {recentLayers.map(layerKey => (
                <Badge
                  key={layerKey}
                  colorScheme="purple"
                  variant="subtle"
                  cursor="pointer"
                  onClick={() => handleLayerToggle(layerKey)}
                  fontSize="2xs"
                  px={2}
                  py={1}
                  borderRadius="md"
                  _hover={{ bg: 'purple.100' }}
                >
                  {labelOf(layerKey)}{/* --- UPDATE: label, not the key */}
                </Badge>
              ))}
            </HStack>
          </Box>
        )}

        {/* Layer Groups */}
        <VStack 
          spacing={0} 
          align="stretch" 
          overflowY="auto" 
          maxH="50vh" 
          pr={2}
          css={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: borderColor, borderRadius: '4px' },
          }}
        >
          {Object.entries(filteredLayers).map(([groupKey, group]) =>
            renderLayerGroup(groupKey, group)
          )}
        </VStack>

        {/* No results */}
        {Object.keys(filteredLayers).length === 0 && (
          <Box textAlign="center" py={8}>
            <Icon as={FaSearch} boxSize={8} color={mutedText} mb={2} />
            <Text color={mutedText} fontSize="sm">
              No layers match your search
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DigitalTwinLayerManager;